/**
 * V5 workbench "Aircraft" mode — a VSP-like aircraft geometry workbench.
 *
 * Loads a VSP model through the fast exact-Bezier backend
 * (`/geometry/aircraft/build` -> OpenVSP's real m_Surface control nets, ~100ms,
 * machine-precision, no EGADS/point-fit) and shows it in an orbitable 3-D viewer
 * with a per-component browser. The compact control-net model ("the model, not
 * the mesh") is re-tessellatable at any resolution.
 *
 * Models come from `/geometry/aircraft/catalog` (the prebaked bundles + the
 * scanned VSP fleet dir), with the bundled four as a hard fallback so the
 * screen never regresses when the catalog endpoint is unavailable. When the
 * live VSP engine is configured, `/geometry/aircraft/parms` exposes the
 * model's design parms as a live-edit panel — overrides are applied
 * worker-side (SetParmVal + Update) on a debounced rebuild.
 *
 * Successor to the previous "hangar" (frontend/workspace/apps/flight) — same
 * intent (aircraft geometry workbench), rebuilt on the V5 surface + the fast path.
 *
 * Two modes: BROWSE (this file — the read-only catalog workbench above) and
 * DESIGN (AircraftDesignPanel — project-owned editable .vsp3 designs with a
 * persisted ops log). The toolbar toggle switches between them.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";

import {
  buildAircraft,
  fetchAircraftCatalog,
  fetchAircraftParms,
  type AircraftBuildResponse,
  type AircraftCatalogModel,
  type AircraftGeomParms,
} from "../../api/aircraft";
import { listExamples } from "../../api/controlSurface";
import { AircraftDesignPanel, AircraftModeToggle, type AircraftMode } from "./AircraftDesignPanel";
import { AircraftParamPanel } from "./AircraftParamPanel";
import { AircraftViewer3D } from "./AircraftViewer3D";
import { publishSourceSeed } from "./csSourceSeed";
import type { SurfaceProps } from "./ThreadV5Surfaces";

/** The four bundled models every deployment has — the catalog fallback, so the
 *  screen never regresses when `/geometry/aircraft/catalog` is unavailable. */
const FALLBACK_MODELS: AircraftCatalogModel[] = [
  { key: "boeing777", label: "Boeing 777-200ER", kind: "prebaked" },
  { key: "dornier228", label: "Dornier 228", kind: "prebaked" },
  { key: "su57", label: "Sukhoi Su-57", kind: "prebaked" },
  { key: "cessna172", label: "Cessna 172", kind: "prebaked" },
];
const RESOLUTIONS = [8, 12, 16, 24];

/** Prebaked model key -> the example STEP stem the backend's examples endpoint
 *  lists it under (only the stems differ; the dir is the backend's). */
const MODEL_STEP_STEMS: Record<string, string> = {
  boeing777: "boeing777200er",
  dornier228: "dornier228",
  su57: "su57",
  cessna172: "cessna172",
};

/** Fleet catalog keys are 'fleet_' + the .vsp3 file stem. */
const FLEET_PREFIX = "fleet_";

/** The example-STEP stem a model key bridges to ('fleet_f16' -> 'f16'); fleet
 *  stems match the CS examples stems, so the CS bridge generalizes. */
function modelStem(key: string): string | undefined {
  if (key.startsWith(FLEET_PREFIX)) return key.slice(FLEET_PREFIX.length).toLowerCase();
  return MODEL_STEP_STEMS[key];
}

/** Lowercased file stem of a path ('/x/cessna172.stp' -> 'cessna172'). */
function pathStem(path: string): string {
  const base = path.split("/").pop() ?? path;
  return base.replace(/\.(stp|step|stl)$/i, "").toLowerCase();
}

export function AircraftSurface({ projectId, persistedProject, onOpenView }: SurfaceProps) {
  const [mode, setMode] = useState<AircraftMode>("browse");
  const [models, setModels] = useState<AircraftCatalogModel[]>(FALLBACK_MODELS);
  const [modelKey, setModelKey] = useState("boeing777");
  const [resolution, setResolution] = useState(12);
  const [result, setResult] = useState<AircraftBuildResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [visible, setVisible] = useState<Record<string, boolean>>({});
  const [selected, setSelected] = useState<string | null>(null);
  const [bridging, setBridging] = useState(false);
  /** Live design parms (null = none/unavailable -> panel hidden quietly). */
  const [parmGeoms, setParmGeoms] = useState<AircraftGeomParms[] | null>(null);
  const [overrides, setOverrides] = useState<Record<string, number>>({});

  // Refs so debounced/async work always reads the latest values without
  // re-arming timers: overrides mirror, build sequencing, pending-rebuild timer.
  const overridesRef = useRef<Record<string, number>>({});
  const buildSeq = useRef(0);
  const rebuildTimer = useRef<number | null>(null);
  const buildArgs = useRef({ key: modelKey, res: resolution });
  buildArgs.current = { key: modelKey, res: resolution };

  // Catalog: fetched once; any failure keeps the bundled fallback in place.
  useEffect(() => {
    let alive = true;
    fetchAircraftCatalog()
      .then((res) => {
        if (alive && res.models?.length) setModels(res.models);
      })
      .catch(() => {
        /* older API / endpoint off -> FALLBACK_MODELS already shown */
      });
    return () => {
      alive = false;
    };
  }, []);

  const build = useCallback((key: string, res: number, force = false) => {
    const seq = ++buildSeq.current;
    const ov = overridesRef.current;
    setLoading(true);
    setError(null);
    buildAircraft({
      model_key: key,
      resolution: res,
      force,
      parm_overrides: Object.keys(ov).length ? ov : undefined,
    })
      .then((r) => {
        if (seq !== buildSeq.current) return; // a newer build superseded this one
        setResult(r);
        // Preserve visibility + selection across parm rebuilds (component names
        // persist); a fresh model's new names default to visible.
        setVisible((v) => Object.fromEntries(r.components.map((c) => [c.name, v[c.name] !== false])));
        setSelected((s) => (s && r.components.some((c) => c.name === s) ? s : null));
      })
      .catch((e) => {
        if (seq === buildSeq.current) setError(String(e?.message ?? e));
      })
      .finally(() => {
        if (seq === buildSeq.current) setLoading(false);
      });
  }, []);

  useEffect(() => {
    build(modelKey, resolution);
  }, [modelKey, resolution, build]);

  // Design parms for the current model. Prebaked models without a .vsp3 (or an
  // unconfigured engine) 4xx/503 here — hide the panel quietly, never error.
  useEffect(() => {
    let alive = true;
    setParmGeoms(null);
    fetchAircraftParms({ modelKey })
      .then((res) => {
        if (alive) setParmGeoms((res.geoms ?? []).filter((g) => g.parms.length > 0));
      })
      .catch(() => {
        if (alive) setParmGeoms(null);
      });
    return () => {
      alive = false;
    };
  }, [modelKey]);

  const clearPendingRebuild = () => {
    if (rebuildTimer.current !== null) {
      window.clearTimeout(rebuildTimer.current);
      rebuildTimer.current = null;
    }
  };
  useEffect(() => clearPendingRebuild, []);

  /** Debounced (500ms) rebuild with the accumulated overrides. */
  const scheduleRebuild = useCallback(() => {
    clearPendingRebuild();
    rebuildTimer.current = window.setTimeout(() => {
      rebuildTimer.current = null;
      build(buildArgs.current.key, buildArgs.current.res);
    }, 500);
  }, [build]);

  const commitOverrides = useCallback(
    (next: Record<string, number>) => {
      overridesRef.current = next;
      setOverrides(next);
      scheduleRebuild();
    },
    [scheduleRebuild],
  );

  const setParm = useCallback(
    (parmId: string, value: number) => commitOverrides({ ...overridesRef.current, [parmId]: value }),
    [commitOverrides],
  );

  const resetGeom = useCallback(
    (geom: AircraftGeomParms) => {
      const ids = new Set(geom.parms.map((p) => p.id));
      const kept = Object.entries(overridesRef.current).filter(([id]) => !ids.has(id));
      if (kept.length === Object.keys(overridesRef.current).length) return;
      commitOverrides(Object.fromEntries(kept));
    },
    [commitOverrides],
  );

  const resetAll = useCallback(() => {
    if (Object.keys(overridesRef.current).length === 0) return;
    commitOverrides({});
  }, [commitOverrides]);

  /** Model switch: drop pending rebuilds + overrides + per-component view state
   *  (geom names like "WingGeom" repeat across models, so preserved visibility
   *  would leak between aircraft), then let the build effect run. */
  const selectModel = (key: string) => {
    clearPendingRebuild();
    overridesRef.current = {};
    setOverrides({});
    setParmGeoms(null);
    setVisible({});
    setSelected(null);
    setModelKey(key);
  };

  const toggle = (name: string) => setVisible((v) => ({ ...v, [name]: v[name] === false }));

  // Bridge to the Control Surfaces workbench: find this model's example STEP via
  // the backend's examples listing (never a hardcoded dir), publish the one-shot
  // source seed, and navigate. Matching is by stem — prebaked keys map through
  // MODEL_STEP_STEMS, fleet keys are the stem itself — preferring the
  // aircraft-screen group, then any STEP. No matching example (dir unset, older
  // API, not a member) degrades to a label-only seed - the CS surface shows its
  // normal empty source state.
  const designControlSurfaces = useCallback(async () => {
    const label = models.find((m) => m.key === modelKey)?.label ?? modelKey;
    const stem = modelStem(modelKey);
    setBridging(true);
    try {
      const res = await listExamples(projectId);
      const candidates = stem
        ? (res.examples ?? []).filter((ex) => ex.key === stem || pathStem(ex.path) === stem)
        : [];
      const match =
        candidates.find((ex) => ex.group === "aircraft-screen") ??
        candidates.find((ex) => ex.kind === "step");
      publishSourceSeed(match ? { stepPath: match.path, label } : { label });
    } catch {
      publishSourceSeed({ label });
    } finally {
      setBridging(false);
    }
    onOpenView("control-surfaces");
  }, [modelKey, models, onOpenView, projectId]);

  const stats = useMemo(() => {
    if (!result) return null;
    return {
      patches: result.total_patches.toLocaleString(),
      triangles: result.total_triangles.toLocaleString(),
      ms: Math.round(result.build_ms),
      cache: result.cache_hit,
    };
  }, [result]);

  const prebaked = models.filter((m) => m.kind !== "fleet");
  const fleet = models.filter((m) => m.kind === "fleet");

  // DESIGN mode: project-owned editable .vsp3 designs (all hooks above still
  // run — the modes share catalog state, and hook order must not change).
  if (mode === "design") {
    return (
      <AircraftDesignPanel
        projectId={projectId}
        persistedProject={persistedProject}
        currentModel={models.find((m) => m.key === modelKey) ?? null}
        modeToggle={<AircraftModeToggle mode="design" onChange={setMode} />}
        onOpenView={onOpenView}
      />
    );
  }

  return (
    <div style={S.root}>
      <div style={S.toolbar}>
        <span style={S.title}>Aircraft</span>
        <AircraftModeToggle mode="browse" onChange={setMode} />
        <select style={S.select} value={modelKey} onChange={(e) => selectModel(e.target.value)}>
          <optgroup label="Aircraft screen">
            {prebaked.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
          </optgroup>
          {fleet.length > 0 && (
            <optgroup label="VSP fleet">
              {fleet.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
            </optgroup>
          )}
        </select>
        <label style={S.field}>
          res
          <select style={S.select} value={resolution} onChange={(e) => setResolution(Number(e.target.value))}>
            {RESOLUTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </label>
        <button style={S.button} onClick={() => build(modelKey, resolution, true)} disabled={loading}>
          {loading ? "Building…" : "Rebuild"}
        </button>
        <button
          style={S.buttonGhost}
          onClick={() => { void designControlSurfaces(); }}
          disabled={bridging}
        >
          {bridging ? "Opening…" : "Design control surfaces"}
        </button>
        {stats && (
          <span style={S.stat}>
            {stats.patches} Bézier patches · {stats.triangles} tris · {stats.ms} ms
            {stats.cache ? " · cached" : ""}
          </span>
        )}
      </div>

      <div style={S.body}>
        <div style={S.viewer}>
          {error && !result
            ? <div style={S.error}>{error}</div>
            : (
              <>
                <AircraftViewer3D
                  modelUrl={result?.model_url ?? null}
                  visible={visible}
                  selected={selected}
                  onSelect={setSelected}
                />
                {/* Rebuilds keep the last good model on screen — slim overlays only. */}
                {loading && result && <div style={S.busy}>Rebuilding…</div>}
                {error && result && <div style={S.errorBanner}>{error}</div>}
              </>
            )}
        </div>

        <div style={S.panel}>
          <div style={S.panelHead}>Components</div>
          {result?.components.map((c) => (
            <div
              key={c.geom_id}
              style={{ ...S.comp, ...(selected === c.name ? S.compSel : null) }}
              onClick={() => setSelected(c.name === selected ? null : c.name)}
            >
              <input
                type="checkbox"
                checked={visible[c.name] !== false}
                onChange={(e) => { e.stopPropagation(); toggle(c.name); }}
                onClick={(e) => e.stopPropagation()}
              />
              <span style={S.compName}>{c.name}</span>
              <span style={S.compMeta}>{c.patches} pat</span>
            </div>
          ))}
          {parmGeoms && parmGeoms.length > 0 && (
            <AircraftParamPanel
              geoms={parmGeoms}
              overrides={overrides}
              disabled={loading}
              onChange={setParm}
              onResetGeom={resetGeom}
              onResetAll={resetAll}
            />
          )}
          <div style={S.note}>
            Exact geometry from OpenVSP's resampled Bézier surfaces — the compact
            control-net model, re-tessellatable to any resolution. No EGADS, no fit.
          </div>
        </div>
      </div>
    </div>
  );
}

const S: Record<string, CSSProperties> = {
  root: { display: "flex", flexDirection: "column", height: "100%", width: "100%",
    background: "var(--v5-surface)", color: "var(--v5-ink)", font: "13px var(--v5-sans, system-ui)" },
  toolbar: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10, padding: "8px 12px",
    borderBottom: "1px solid var(--v5-line)" },
  title: { fontWeight: 600, fontSize: 14, letterSpacing: 0.2 },
  field: { display: "flex", alignItems: "center", gap: 4, color: "var(--v5-ink)", opacity: 0.8 },
  select: { background: "var(--v5-surface)", color: "var(--v5-ink)", border: "1px solid var(--v5-line)",
    borderRadius: "var(--v5-radius, 4px)", padding: "3px 6px", font: "inherit" },
  button: { background: "var(--v5-primary)", color: "#fff", border: "none",
    borderRadius: "var(--v5-radius, 4px)", padding: "4px 12px", cursor: "pointer", font: "inherit" },
  buttonGhost: { background: "var(--v5-surface)", color: "var(--v5-ink)",
    border: "1px solid var(--v5-line)", borderRadius: "var(--v5-radius, 4px)",
    padding: "4px 12px", cursor: "pointer", font: "inherit" },
  stat: { marginLeft: "auto", font: "12px var(--v5-mono, monospace)", opacity: 0.75 },
  body: { display: "flex", flex: 1, minHeight: 0 },
  viewer: { flex: 1, minWidth: 0, position: "relative" },
  panel: { width: 240, borderLeft: "1px solid var(--v5-line)", display: "flex",
    flexDirection: "column", overflowY: "auto" },
  panelHead: { padding: "8px 12px", fontWeight: 600, borderBottom: "1px solid var(--v5-line)",
    fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5, opacity: 0.7 },
  comp: { display: "flex", alignItems: "center", gap: 8, padding: "6px 12px", cursor: "pointer",
    borderBottom: "1px solid var(--v5-line)" },
  compSel: { background: "var(--v5-primary-soft, rgba(80,120,180,0.15))" },
  compName: { flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  compMeta: { font: "11px var(--v5-mono, monospace)", opacity: 0.6 },
  note: { padding: 12, fontSize: 11, opacity: 0.6, lineHeight: 1.5, marginTop: "auto" },
  error: { position: "absolute", inset: 0, display: "grid", placeItems: "center",
    padding: 24, textAlign: "center", color: "var(--v5-amber, #c80)", font: "13px var(--v5-mono, monospace)" },
  busy: { position: "absolute", top: 8, left: 8, padding: "2px 10px",
    background: "var(--v5-surface)", border: "1px solid var(--v5-line)",
    borderRadius: "var(--v5-radius, 4px)", font: "11px var(--v5-mono, monospace)", opacity: 0.85 },
  errorBanner: { position: "absolute", bottom: 8, left: 8, right: 8, padding: "4px 10px",
    background: "var(--v5-surface)", border: "1px solid var(--v5-line)",
    borderRadius: "var(--v5-radius, 4px)", color: "var(--v5-amber, #c80)",
    font: "12px var(--v5-mono, monospace)" },
};
